import { useState } from 'react';
import { IReadonlyContentDetails } from './types';
import SubmissionFlowHeader from '@common/components/SubmissionFlowHeader/SubmissionFlowHeader';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import { selectProductGroup } from '@common/features/productGroup/slices/productGroup/selectors';
import { DocumentType } from '@common/types';
import * as Styled from './styles';
import { useFetchPublicProductGroupState } from '@common/features/productGroup/hooks/useFetchPublicProductGroupState';
import Tabs from '@common/components/Tabs/Tabs';
import { ReadonlyDocumentContent } from '../../components/DocumentContentDetails/ReadonlyDocumentContent';
import DocumentDetailsSkeletonLoader from './DocumentDetailsSkeletonLoader';
import { HtmlDocument } from '@components/HtmlDocument/HtmlDocument';
import { htmlFileIsAvailable } from '@features/document/helpers';
import { DocumentVersionHistory } from '../../components/DocumentVersionHistory/DocumentVersionHistory';
import { documentTypeToCustomLabelMap } from '@common/constants';
import { DocumentProcessingFlow } from '../../components/DocumentContentDetails/types';
import { getDocumentAttributes, getEmcShortLinkDetails } from '../../helpers';
import { useFetchDocumentHtmlContent } from '@common/hooks/useFetchDocumentHtmlContent';
import { useAppSelector } from '@common/hooks/redux';
import { formatDocumentSubTitle } from '@common/utils/utils';
import { selectConfiguration } from '@common/features/configuration/selectors';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { Button, ButtonTypes } from '@common/components';
import theme from '@common/theme';
import { useFetchCompanyConfiguration } from '@common/features/configuration/useFetchCompanyConfiguration';

const PublishedDocumentDetailsContent = ({
  showHistoryFiltering,
  contentType,
  productGroupId,
  documentId,
  linkedProductId,
  isAdmin = false,
  printUrl,
  onCancel,
  onFileDownload,
}: IReadonlyContentDetails) => {
  const productGroup = useAppSelector(selectProductGroup);
  const { showPrintButton } = useFeatureFlags();

  const { publicProductGroupState, isFetchingPublicProductGroupState, isPublicProductGroupStateFetched } =
    useFetchPublicProductGroupState(productGroupId!);
  const { showQrCodeForPil } = useFeatureFlags();
  const configuration = useAppSelector(selectConfiguration);
  const document = publicProductGroupState?.documents.find((d) => d.documentId === Number.parseInt(documentId!) && d.type === contentType);
  const documentTitle = document?.documentTitle;
  const fileName = document?.originalFileName;
  const version = document?.version ?? 1;
  const approvalDate = document?.regulatorApprovalDate;
  const approved = !!(document?.regulatorApprovalDate ?? document?.approvedDate);
  const firstAuthorisationDate = document?.authorisedDate;
  const regulator = document?.approvedByRegulator;
  const reasonsForSubmission = document?.reasonsForSubmission;
  const documentState = document?.state;
  const hasDigitalVersion = document?.hasDigitalVersion ?? null;
  const url = document?.link ?? null;
  const details = document?.details ?? '';

  const reasonForRetirement =
    document?.reasonForRetirement?.length! > 0
      ? { label: document?.reasonForRetirement as string, value: document?.reasonForRetirement as string }
      : undefined;

  const displayHtmlView = !!document && htmlFileIsAvailable(document, publicProductGroupState!.documents);
  
  const documentProcessingDetails = {
    documentProcessingFlow: DocumentProcessingFlow.standard,
    isCustomerApprovalRequired: null,
    customerUrl: undefined,
  };

  const attributes = getDocumentAttributes(contentType, isAdmin);
  const { companyConfiguration } = useFetchCompanyConfiguration(publicProductGroupState?.productGroup?.companyId!);

  const onPrint = () => {
    window.open(
      (printUrl ?? '').replace(':documentId', document!.documentId.toString())
        .replace(':documentType', document!.type.toString())
        .replace(':version', document!.version.toString()),
      '_blank'
    );
  };

  const { emcShortLink, emcUrlTitle } = getEmcShortLinkDetails(attributes, linkedProductId, contentType, configuration.emcShortLink, companyConfiguration);

  const [selectedTab, setSelectedTab] = useState<string>(`${documentTypeToCustomLabelMap[contentType]} details`);
  
  const { data: publishedHtml, documentFetching } = useFetchDocumentHtmlContent(
    attributes.hasHtmlContent
      ? {
          submissionId: null,
          documentId: Number(documentId),
          version,
          type: contentType,
        }
      : undefined,
  );

  // The Print button is shown only if:
  // - the feature flag is enabled, and
  // - the user is an admin, or
  // - the document is not an SMPC, or
  // - the document has TVT files, and
  // - the selected tab is 'View html'
  const canPrint = showPrintButton && (
    isAdmin
    || (!!document ? document?.type !== DocumentType.Smpc : false)
    || Boolean(document?.hasTvtFiles)
  ) && selectedTab === 'View html';

  const getTabs = () => {
    const items = [
      {
        label: `${documentTypeToCustomLabelMap[contentType]} details`,
        content: (
          <ReadonlyDocumentContent
            documentTitle={documentTitle ?? ''}
            version={version ?? 1}
            fileName={fileName ?? ''}
            contentType={contentType}
            approvalDate={approvalDate ?? null}
            approved={approved}
            url={url}
            firstAuthorisationDate={firstAuthorisationDate ?? null}
            regulator={regulator ?? ''}
            reasonsForSubmission={reasonsForSubmission ?? []}
            reasonForRetirement={reasonForRetirement}
            documentState={documentState!}
            hasDigitalVersion={hasDigitalVersion}
            onFileClick={onFileDownload}
            contentDescription={document?.contentDescription ?? ''}
            isVisibleToHCP={document?.isVisibleToHCP ?? null}
            htmlContent={publishedHtml}
            onAlternativeTextFileNameClick={() => {}}
            isAdmin={isAdmin}
            secondaryFileName={undefined}
            documentProcessingDetails={documentProcessingDetails}
            details={details}
            isAccessiblePilRequested={document?.isAccessiblePilRequested}
          />
        ),
      },
      {
        label: `Version history`,
        content: (
          <DocumentVersionHistory
            isAdmin={isAdmin}
            documentId={document?.documentId ?? 0}
            documentType={document?.type}
            showHistoryFiltering={showHistoryFiltering}
          />
        ),
      },
    ];

    if (displayHtmlView)
      items.push({
        label: `View html`,
        content: <HtmlDocument documentId={documentId} documentType={contentType} version={version.toString()} />,
      });

    return items;
  };

  const renderContent = () => {
    switch (contentType) {
      case DocumentType.Smpc:
      case DocumentType.Pil:
      case DocumentType.Rmm:
      case DocumentType.Dhcp:
      case DocumentType.UserManual:
      case DocumentType.SafetyAlert:
      case DocumentType.ProductInformation:
      case DocumentType.Audio:
      case DocumentType.Video:
        return <Tabs items={getTabs() }
                  selectedTab={selectedTab}
                  onTabSelected={(selectedTab) => setSelectedTab(selectedTab)}
                  rightSideNode={
                    canPrint ? (
                      <Button
                        type={ButtonTypes.PRIMARY}
                        text="Print"
                        height={35}
                        width={170}
                        color={theme.elements.defaultWhite}
                        onClick={onPrint}
                        tooltip='Click here to see the html view, and print or generate a PDF. Please note that the PDF output will depend on the tool within your browser and there may be some differences between its presentation and how the document appears on emc - e.g. some special characters and table width may be rendered differently when generating a PDF using different browsers.'
                      />
                    ) : null
                  } />;
      default:
        return <>Work in progress</>;
    }
  };

  if (isFetchingPublicProductGroupState || !isPublicProductGroupStateFetched) {
    return <DocumentDetailsSkeletonLoader />;
  }

  return (
    <>
      <Styled.ContentDetailsWrap>
        <SubmissionFlowHeader
          title={formatDocumentSubTitle(isAdmin, productGroup.name, document, false)}
          onBackClick={onCancel}
          onCloseClick={onCancel}
          emcUrl={emcShortLink}
          showQrCode={showQrCodeForPil}
          emcUrlTitle={emcUrlTitle}
        />
        {!documentFetching && renderContent()}
      </Styled.ContentDetailsWrap>
      <SubmissionFlowFooter onCancel={onCancel} cancelText="Back" />
    </>
  );
};

export default PublishedDocumentDetailsContent;
